import { FormattedMessage } from 'react-intl';
import { Box, Divider, IconButton } from '@mui/material';
import { Link, useParams } from 'react-router';
import { graphql, useFragment } from 'react-relay';
import { cpsPageUrlAnchor } from '@/pages/CPS';
//import { useIsGranted } from '@/utils/useIsGranted';
import { Paragraph } from '../Paragraph';
import { Paper } from '../Paper';
import { H2 } from '../H2';
import { AddIcon } from '../icons';
import type { ProjectsEmptyState_pqiUser$key } from './__generated__/ProjectsEmptyState_pqiUser.graphql';
import type { ProjectsEmptyState_team$key } from './__generated__/ProjectsEmptyState_team.graphql';

type ProjectsEmptyStateProps = {
    pqiUser: ProjectsEmptyState_pqiUser$key;
    team: ProjectsEmptyState_team$key;
};

export function ProjectsEmptyState(props: ProjectsEmptyStateProps) {
    const pqiUser = useFragment(fragments.pqiUser, props.pqiUser);
    const team = useFragment(fragments.team, props.team);
    //const isGranted = useIsGranted(pqiUser);
    const params = useParams<{ teamPath: string }>();

    if (!team) {
        throw new Error('Team not found');
    }

    const canCreateProject = pqiUser.grants.some(
        ({ role, teamId, portfolioId, projectId }) =>
            (
                [
                    'TEAM_ADMIN',
                    'TEAM_OWNER',
                    'SURVEY_ADMIN',
                    'PROJECT_CREATOR',
                ] as (typeof role)[]
            ).includes(role) &&
            ((teamId === null && portfolioId === null && projectId === null) ||
                teamId === team.id ||
                portfolioId === team.portfolio.id),
    );
    //     isGranted(
    //     (grant) =>
    //         [
    //             'ProjectMeasuring.ProjectCreator',
    //             'ProjectMeasuring.TeamAdmin',
    //             'ProjectMeasuring.TeamOwner',
    //         ].includes(grant.roleName) ||
    //         (grant.roleName === 'ProjectMeasuring.SurveyAdmin' &&
    //             (grant.resourceName.startsWith('ProjectMeasuring.Team.') ||
    //                 grant.resourceName.startsWith(
    //                     'ProjectMeasuring.Portfolio.',
    //                 ))),
    // );
    return (
        <Paper>
            <Box
                sx={{
                    margin: '50px auto 150px',
                    verticalAlign: `center`,
                    textAlign: `center`,
                    height: '500px',
                    padding: '20px',
                    boxSizing: 'border-box',
                }}
            >
                <H2>
                    <FormattedMessage defaultMessage="You have no projects yet" />
                </H2>
                {canCreateProject && (
                    <>
                        <Divider />
                        {/* todo Some pqiUser roles does not have access to creating a project, but it is
                         highly unlikely that we add them before adding projects. We´ll leave
                         it as an edge-case. */}
                        <IconButton
                            component={Link}
                            to={`/${params.teamPath}/${cpsPageUrlAnchor}`}
                            sx={(theme) => ({
                                color: theme.bino.color.gray,
                                p: 2,
                                my: 2,
                            })}
                        >
                            <AddIcon size={200} />
                        </IconButton>
                        <Paragraph
                            sx={{
                                padding: '30px',
                                fontSize: {
                                    xs: 18,
                                    md: 24,
                                },
                                lineHeight: 1.4,
                                textAlign: 'center',
                            }}
                        >
                            <FormattedMessage defaultMessage="This portfolio is empty. Get started by adding your first project." />
                        </Paragraph>{' '}
                    </>
                )}
            </Box>
        </Paper>
    );
}

const fragments = {
    team: graphql`
        fragment ProjectsEmptyState_team on Team
        @argumentDefinitions(portfolioPath: { type: "String" }) {
            id
            portfolio(path: $portfolioPath) {
                id
            }
        }
    `,
    pqiUser: graphql`
        fragment ProjectsEmptyState_pqiUser on PQiUser {
            grants {
                role
                teamId
                portfolioId
                projectId
            }
        }
    `,
};

export default ProjectsEmptyState;
